import { createSlice, createSelector, type PayloadAction } from '@reduxjs/toolkit';
import type { MapState, MarkerData } from '../types/map.types';
import type { RootState } from '../store';

/**
 * Initial state for the map slice.
 */
const initialState: MapState = {
  markers: [],
  polygons: [],
  selectedPlaceId: null,
};

/**
 * Map slice for managing client-side map state (markers, polygons, active selection).
 */
const mapSlice = createSlice({
  name: 'map',
  initialState,
  reducers: {
    /**
     * Replace the full set of markers on the map.
     */
    setMarkers(state, action: PayloadAction<MarkerData[]>) {
      state.markers = action.payload;
    },

    /**
     * Reset the entire map state back to its initial values.
     */
    clearMap() {
      return initialState;
    },

    /**
     * Set (or clear) the active marker by `placeId`.
     */
    setSelectedPlace(state, action: PayloadAction<string | null>) {
      state.selectedPlaceId = action.payload;
    },
  },
});

// Export actions
export const { setMarkers, clearMap, setSelectedPlace } = mapSlice.actions;

// Selectors

/**
 * Narrow input selectors — one per field — so each memoized selector only
 * recomputes when its own slice changes, not on unrelated map state updates.
 */
const selectMarkersRaw = (state: RootState): MarkerData[] => state.map.markers;
const selectSelectedPlaceIdRaw = (state: RootState): string | null => state.map.selectedPlaceId;

/**
 * Select the markers array (memoized).
 */
export const selectMarkers = createSelector([selectMarkersRaw], (markers): MarkerData[] => markers);

/**
 * Select the `placeId` of the currently active marker, or `null` (memoized).
 */
export const selectSelectedPlaceId = createSelector(
  [selectSelectedPlaceIdRaw],
  (id): string | null => id,
);

// Export reducer
export default mapSlice.reducer;
